import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LAST_UPDATED = '20 de Maio de 2026';
const LAST_UPDATED_EN = 'May 20, 2026';

const COPY = {
  pt: {
    eyebrow: 'Política de Privacidade',
    title: 'O tratamento que damos aos seus dados.',
    lead:
      'A Your Broker Portugal trata os dados pessoais que nos confia com a mesma discrição que aplica em qualquer mandato. Esta página resume — em linguagem clara, sem juridiquês desnecessário — quem recolhe, o quê, porquê, durante quanto tempo, e quais os seus direitos.',
    lastUpdated: `Última atualização: ${LAST_UPDATED}`,
    seoTitle: 'Política de Privacidade | Your Broker Portugal',
    seoDescription:
      'Como a Your Broker Portugal recolhe, trata e protege os dados pessoais dos seus clientes e visitantes. Conforme o RGPD (UE 2016/679).',

    sections: [
      {
        title: '01 · Responsável pelo tratamento',
        body: [
          'O responsável pelo tratamento dos seus dados é a Your Broker Portugal, com sede em Portugal, AMI 19715, NIPC 516 549 642.',
          'Para qualquer questão sobre privacidade, escreva para portugal@yourbrokerportugal.com.',
        ],
      },
      {
        title: '02 · Que dados recolhemos',
        body: [
          'Quando submete um pedido de contacto, recolhemos: nome, email, telefone/WhatsApp e a mensagem que nos envia. Nada mais.',
          'Quando navega no site, o nosso servidor regista automaticamente endereço IP, tipo de browser, páginas visitadas e tempo de permanência — informação técnica habitual, anonimizada para fins estatísticos.',
          'Não recolhemos dados sensíveis (saúde, religião, opiniões políticas, orientação sexual) em nenhum ponto do site.',
        ],
      },
      {
        title: '03 · Para que usamos os seus dados',
        body: [
          'Responder ao seu pedido de contacto e, se aplicável, dar seguimento a um briefing privado.',
          'Cumprir obrigações legais portuguesas (faturação, AMI, anti-branqueamento de capitais para clientes que avancem para mandato).',
          'Enviar comunicações editoriais (Journal) — apenas se subscrever explicitamente. Pode cancelar a qualquer momento.',
          'Análise estatística agregada do site, via Google Analytics — anonimizada, sem perfis individuais.',
        ],
      },
      {
        title: '04 · Com quem partilhamos',
        body: [
          'Nunca vendemos os seus dados. Nunca os partilhamos com terceiros para fins comerciais.',
          'Apenas partilhamos com: (a) prestadores técnicos que nos servem (hosting, email, CRM) sob contratos de subprocessamento conformes ao RGPD; (b) autoridades portuguesas quando legalmente obrigatório; (c) o seu próprio advogado ou consultor, se nos autorizar expressamente no âmbito do seu mandato.',
        ],
      },
      {
        title: '05 · Durante quanto tempo',
        body: [
          'Pedidos de contacto sem seguimento: 12 meses, depois eliminados.',
          'Dados de clientes com mandato ativo: durante o mandato e até 10 anos após o fim, por obrigação legal (AMI, contabilidade).',
          'Subscritores do Journal: enquanto mantiverem a subscrição.',
          'Logs técnicos do servidor: até 6 meses.',
        ],
      },
      {
        title: '06 · Os seus direitos',
        body: [
          'Pode, a qualquer momento, exercer os seus direitos RGPD: acesso, retificação, apagamento ("direito a ser esquecido"), portabilidade, oposição e limitação do tratamento.',
          'Basta escrever para portugal@yourbrokerportugal.com. Respondemos em 30 dias, sem custo, sem perguntas.',
          'Se considerar que tratámos os seus dados indevidamente, pode também apresentar reclamação à Comissão Nacional de Proteção de Dados (CNPD) — www.cnpd.pt.',
        ],
      },
      {
        title: '07 · Cookies',
        body: [
          'Usamos cookies estritamente necessários (sessão, segurança) sem requererem consentimento, e cookies analíticos (Google Analytics) apenas após o seu consentimento explícito no banner de cookies.',
          'Pode rejeitar os cookies analíticos a qualquer momento. O site funcionará normalmente sem eles.',
        ],
      },
      {
        title: '08 · Segurança',
        body: [
          'O site usa HTTPS em todas as páginas. A base de dados é gerida em infraestrutura europeia (Lisboa). Acessos administrativos protegidos por autenticação forte. Em caso de violação de dados que afete os seus direitos, notificamos a CNPD e os afetados dentro de 72 horas, conforme o RGPD exige.',
        ],
      },
      {
        title: '09 · Alterações',
        body: [
          `Esta política pode ser atualizada para refletir mudanças legais ou operacionais. A data de última atualização aparece sempre no topo. Versão atual: ${LAST_UPDATED}.`,
        ],
      },
    ],
  },
  en: {
    eyebrow: 'Privacy Policy',
    title: 'How we treat your data.',
    lead:
      'Your Broker Portugal treats the personal data you entrust to us with the same discretion we bring to every mandate. This page summarises — in clear language, free of unnecessary legalese — who collects what, why, for how long, and your rights.',
    lastUpdated: `Last updated: ${LAST_UPDATED_EN}`,
    seoTitle: 'Privacy Policy | Your Broker Portugal',
    seoDescription:
      'How Your Broker Portugal collects, processes and protects the personal data of clients and visitors. GDPR compliant (EU 2016/679).',

    sections: [
      {
        title: '01 · Data controller',
        body: [
          'The controller of your data is Your Broker Portugal, headquartered in Portugal, AMI 19715, NIPC 516 549 642.',
          'For any privacy matter, write to portugal@yourbrokerportugal.com.',
        ],
      },
      {
        title: '02 · What data we collect',
        body: [
          'When you submit a contact enquiry, we collect: name, email, phone/WhatsApp and the message you send us. Nothing more.',
          'When you browse the site, our server automatically logs IP address, browser type, pages visited and time on site — standard technical information, anonymised for statistical purposes.',
          'We never collect sensitive data (health, religion, political opinions, sexual orientation) anywhere on the site.',
        ],
      },
      {
        title: '03 · What we use your data for',
        body: [
          'To respond to your enquiry and, where applicable, to follow up with a private briefing.',
          'To meet Portuguese legal obligations (invoicing, AMI, anti-money-laundering for clients who proceed to mandate).',
          'To send editorial communications (the Journal) — only if you explicitly subscribe. You can unsubscribe at any moment.',
          'Aggregated statistical analysis of the site via Google Analytics — anonymised, no individual profiling.',
        ],
      },
      {
        title: '04 · Who we share with',
        body: [
          'We never sell your data. We never share it with third parties for commercial purposes.',
          'We only share with: (a) technical providers serving us (hosting, email, CRM) under GDPR-compliant data processing agreements; (b) Portuguese authorities when legally required; (c) your own lawyer or advisor, if you explicitly authorise us within the scope of your mandate.',
        ],
      },
      {
        title: '05 · For how long',
        body: [
          'Contact enquiries without follow-up: 12 months, then deleted.',
          'Client data with an active mandate: throughout the mandate and up to 10 years afterwards, by legal obligation (AMI, accounting).',
          'Journal subscribers: while the subscription is active.',
          'Technical server logs: up to 6 months.',
        ],
      },
      {
        title: '06 · Your rights',
        body: [
          'You may at any time exercise your GDPR rights: access, rectification, erasure ("right to be forgotten"), portability, objection and restriction of processing.',
          'Simply write to portugal@yourbrokerportugal.com. We respond within 30 days, free of charge, without questions.',
          'If you believe we have mishandled your data, you may also lodge a complaint with the Portuguese Data Protection Authority (CNPD) — www.cnpd.pt.',
        ],
      },
      {
        title: '07 · Cookies',
        body: [
          'We use strictly necessary cookies (session, security) without requiring consent, and analytical cookies (Google Analytics) only after your explicit consent in the cookie banner.',
          'You may reject analytical cookies at any time. The site will function normally without them.',
        ],
      },
      {
        title: '08 · Security',
        body: [
          'The site uses HTTPS on every page. The database is hosted on European infrastructure (Lisbon). Administrative access is protected by strong authentication. In the event of a data breach affecting your rights, we notify the CNPD and the affected parties within 72 hours, as the GDPR requires.',
        ],
      },
      {
        title: '09 · Changes',
        body: [
          `This policy may be updated to reflect legal or operational changes. The date of last update always appears at the top. Current version: ${LAST_UPDATED_EN}.`,
        ],
      },
    ],
  },
};

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (e.target.classList.add('is-visible'), io.unobserve(e.target))),
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );
    el.querySelectorAll('.rv').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
};

const Eyebrow = ({ children }) => (
  <p
    className="inline-flex items-center gap-3 text-[11px] font-medium uppercase text-editorial-bronze"
    style={{ letterSpacing: '0.24em', fontFamily: 'DM Sans, sans-serif' }}
  >
    <span className="block w-7 h-px bg-editorial-bronze" />
    {children}
  </p>
);

const PrivacyPage = () => {
  const { language } = useLanguage();
  const copy = COPY[language] || COPY.pt;
  const ref = useReveal();
  const [homeContent, setHomeContent] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/home-content`).then((r) => setHomeContent(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title={copy.seoTitle}
        description={copy.seoDescription}
        url={`https://yourbrokerportugal.com${language === 'pt' ? '/pt/privacy' : '/privacy'}`}
      />
      <style>{`
        body { background: #f7f4ef; }
        .rv { opacity: 0; transform: translateY(28px); transition: opacity .95s ease, transform .95s ease; }
        .rv.is-visible { opacity: 1; transform: translateY(0); }
      `}</style>

      <Header companyName={homeContent?.headerFooter?.companyName} logo={homeContent?.headerFooter?.logo} />

      <main ref={ref}>
        {/* HERO */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-20 px-6 md:px-10 lg:px-16 xl:px-24 bg-editorial-mineral">
          <div className="max-w-4xl mx-auto">
            <div className="rv">
              <Eyebrow>{copy.eyebrow}</Eyebrow>
            </div>
            <h1
              className="rv text-editorial-charcoal font-light mt-7 mb-9"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.4rem, 4.4vw, 3.8rem)', lineHeight: '1.08' }}
              data-testid="privacy-hero-title"
            >
              {copy.title}
            </h1>
            <p
              className="rv text-editorial-muted font-light max-w-2xl mb-6"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
            >
              {copy.lead}
            </p>
            <p
              className="rv text-editorial-bronze"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.62rem', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 500 }}
              data-testid="privacy-last-updated"
            >
              {copy.lastUpdated}
            </p>
          </div>
        </section>

        {/* SECTIONS */}
        <section className="px-6 md:px-10 lg:px-16 xl:px-24 py-16 md:py-24 lg:py-32 bg-editorial-sand/40">
          <div className="max-w-3xl mx-auto">
            {copy.sections.map((s, i) => (
              <article key={s.title} className={`rv ${i < copy.sections.length - 1 ? 'pb-12 mb-12 border-b' : ''}`} style={{ borderColor: 'rgba(28,28,26,0.08)' }}>
                <h2
                  className="text-editorial-charcoal font-light mb-6"
                  style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.6rem, 2.4vw, 2rem)', lineHeight: '1.22' }}
                  data-testid={`privacy-section-${i + 1}`}
                >
                  {s.title}
                </h2>
                <div className="space-y-5">
                  {s.body.map((p, idx) => (
                    <p
                      key={idx}
                      className="text-editorial-muted font-light"
                      style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.98rem', lineHeight: '1.95' }}
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>

      <Footer
        data={{
          ...(homeContent?.headerFooter || {}),
          footerDescription:
            language === 'en'
              ? 'Private buying agent for international buyers in Portugal.'
              : 'Consultoria privada para compradores internacionais em Portugal.',
        }}
      />
    </>
  );
};

export default PrivacyPage;
